import { Injectable } from '@angular/core';
import { DeviceType, DriverDetailsResponse, DriverManagementService, DriverManagementVerbs, LoginRequest, LoginRequestCommon } from '@cordic/driver-management';
import { Observable, concatMap, firstValueFrom, from, tap, timeout } from 'rxjs';
import { User } from 'src/app/models/interfaces/user.model';
import { RequestOptionsProviderService } from '../request-options-provider/request-options-provider.service';
import { DeviceStoreService } from '../device-store/device-store.service';

import packageJson from '../../../../package.json';
import { LocalStorageEnum } from 'src/app/models/enum/local-storage.enum';
import { DriverStatusCode } from 'src/app/pages/login/login.component';
import { VariableTypeEnum } from 'src/app/models/enum/variable-type.enum';

export const NEW_FLEET = -1;

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  constructor
  (
    private requestOptions: RequestOptionsProviderService, 
    private deviceStore: DeviceStoreService, 
    private driverManagementService: DriverManagementService
  ) 
  { }

  async login(user: User | string, password: string)
  {
    return await firstValueFrom(this.deviceStore.deviceId.pipe(
      concatMap(async (deviceID) => await this.driverManagementLogin(deviceID, user, password)),
      timeout({ first: 60000 })
    ));
  }

  async driverManagementLogin(deviceID: string, user: string | User, password: string)
  {
    const version = from(Promise.resolve(packageJson.version));
    return await firstValueFrom(version.pipe(
      concatMap( async ([appVersion]) => {
        const commonMembers: LoginRequestCommon = {
          deviceID,
          device: DeviceType.BROWSER,
          appVersion,
        };

        let requestBody: LoginRequest;
        if (typeof user === VariableTypeEnum.STRING)
        {
          requestBody = {
            ...commonMembers,
            loginID: (user as string),
            password,
          };
        }
        else
        {
          requestBody = {
            ...commonMembers,
            driverUserID: (user as User).id,
            password,
          };
        }

        return await firstValueFrom(this.driverManagementService.post(
          DriverManagementVerbs.LOGIN,
          requestBody
        ))
        .then( async (response: any) => {
          this.requestOptions.jwt = response[LocalStorageEnum.JWT];

          this.deviceStore.setSession({
            driverStatus: response.driverStatus,
            verificationRequirement: response.verifyReq,
            canDeleteDriver: response.canDeleteDriver,
            jwt: response.jwt
          });

          localStorage.setItem(LocalStorageEnum.CAN_DEL_DRIVER, response.canDeleteDriver.toString());

          if (response.driverStatus === DriverStatusCode.REGISTERED)
          {
            this.deviceStore.setIsSignedOn(false);
          }
          else
          {
            this.deviceStore.setIsSignedOn(true);
          }

          await firstValueFrom(this.driverManagementDetails(response.driverUserID))
          .then(detailsResponse => {
            if (detailsResponse.demographic)
            {
              this.deviceStore.setUser({
                id: response.driverUserID,
                name: detailsResponse.demographic.fullname,
                pictureUrl: detailsResponse.demographic.mugshot,
                phoneNo: detailsResponse.demographic.mobileTelNo,
              });

              localStorage.setItem(LocalStorageEnum.USER, JSON.stringify({
                id: response.driverUserID,
                name: detailsResponse.demographic.fullname,
                pictureUrl: detailsResponse.demographic.mugshot,
                phoneNo: detailsResponse.demographic.mobileTelNo,
              }));
            }
          });
    
          localStorage.setItem(LocalStorageEnum.JWT, response[LocalStorageEnum.JWT]);
        });
      })
    ));
  }

  driverManagementDetails(driverUserID: number) : Observable<DriverDetailsResponse>
  {
    return this.driverManagementService.post(DriverManagementVerbs.DETAILS, { driverUserID });
  }
}
